import { defineStore } from "pinia";
import { useMasters } from "~/store/masters";
import {
  sameYear,
  getWeek,
  getStartOfWeek,
  getEndOfWeek,
  addDays,
  changeFormatMDY,
  normalizeDate,
  getNextWeek,
} from "~/composables/dates";
import { useStorage } from "@vueuse/core";
import { getDateDMY, parseDateAsUTC } from "~/composables/dates";

export const useCalendar = defineStore("calendar", {
  state: () => {
    const today = parseDateAsUTC();
    const startOfWeek = getStartOfWeek(getNextWeek());

    return {
      selectedDate: getNextWeek(),
      numberWeek: getWeek(getNextWeek()),
      currentWeekStartDate: startOfWeek,
      currentWeekEndDate: getEndOfWeek(getNextWeek()),
      weekDays: [], // se inicializa luego
      weekDaysRange: [],
      weekDaysRangeOnlyDates: [],
      holidaysWeek: [],
      daysToShow: useStorage("calendar-days-to-show", 7),
      startDay: 1,
    };
  },

  actions: {
    initializeCalendar() {
      // Inicializamos la semana al cargar la store
      this.updateWeekDays();
    },

    toggleWeek() {
      this.daysToShow === 7 ? this.setWorkWeek() : this.setFullWeek();
    },

    setWorkWeek() {
      this.daysToShow = 5;
    },

    setFullWeek() {
      this.daysToShow = 7;
    },

    setSelectedDate(date) {
      this.selectedDate = date;
    },

    setNumberWeek() {
      this.numberWeek = getWeek(this.currentWeekStartDate);
    },

    setHolidaysWeek() {
      this.holidaysWeek = [];
      const masters = useMasters();
      const holidaysPerWeek = masters.getHolidaysPerWeek;
      const holidays = holidaysPerWeek[this.numberWeek];

      if (holidays) {
        for (const holiday of holidays) {
          if (sameYear(changeFormatMDY(holiday.FESTIVO), this.selectedDate)) {
            this.holidaysWeek.push(holiday);
          }
        }
      }

    },

    goToPreviousWeek() {
      this.currentWeekStartDate = parseDateAsUTC(
        addDays(this.currentWeekStartDate, -7)
      );
      this.currentWeekEndDate = parseDateAsUTC(
        addDays(this.currentWeekEndDate, -7)
      );
      this.updateWeek();
    },

    goToNextWeek() {
      this.currentWeekStartDate = parseDateAsUTC(
        addDays(this.currentWeekStartDate, 7)
      );
      this.currentWeekEndDate = parseDateAsUTC(
        addDays(this.currentWeekEndDate, 7)
      );
      this.updateWeek();
    },

    goToSelectedWeek() {
      this.selectedDate = this.weekDaysRange[this.weekDaysRange.length - 1];
      this.currentWeekStartDate = getStartOfWeek(this.selectedDate);
      this.currentWeekEndDate = getEndOfWeek(this.selectedDate);
      this.updateWeekDays();
    },

    updateWeek() {
      this.selectedDate = this.currentWeekStartDate;
      this.updateWeekDays();
    },

    isInWeek(date) {
      const dateFormat = getDateDMY(date, "-");
      return this.weekDaysRangeOnlyDates.includes(dateFormat);
    },

    updateWeekDays() {
      // Generamos los días de la semana
      this.weekDays = this.setWeekDays(this.currentWeekStartDate);

      // Creamos rangos según daysToShow
      const limit = 7;
      this.weekDaysRange = this.weekDays.slice(0, limit).map((d) => d.date);
      this.weekDaysRangeOnlyDates = this.weekDays
        .slice(0, limit)
        .map((d) => getDateDMY(d.date, "-"));

      this.setNumberWeek();
      this.setHolidaysWeek();
    },

    setCalendarToday() {
      const today = parseDateAsUTC();
      this.selectedDate = today;
      this.currentWeekStartDate = getStartOfWeek(today);
      this.currentWeekEndDate = getEndOfWeek(today);
      this.updateWeekDays();
    },

    setWeekDays(weekStartDate = this.selectedDate) {
      const nuxtApp = useNuxtApp();
      let week_dates = [];
      if (weekStartDate) {
        for (let i = 0; i < 7; i++) {
          const date = addDays(weekStartDate, i);
          week_dates.push({
            name: nuxtApp.$i18n.t(`common.calendar.weekdays.${i}`),
            date: date,
          });
        }
      }
      return week_dates;
    },
  },

  getters: {
    getWeekDays: (state) => state.weekDays,
    getOnlyWeekDays: (state) => state.weekDays.map((day) => day.date),
    getOnlyWeekDaysRangeOnlyDates: (state) =>
      state.weekDays.map((day) => getDateDMY(day.date, "-")),
    getNumberWeek: (state) => state.numberWeek,
    getHolidaysWeek: (state) => state.holidaysWeek,
    isPreviousCurrentWeek:
      (state) =>
      (date = state.selectedDate) => {
        const monday = getStartOfWeek(parseDateAsUTC());
        return (
          normalizeDate(parseDateAsUTC(date)) <
          normalizeDate(parseDateAsUTC(monday))
        );
      },
    inCalendarNavigationWeek: (state) => (date) => {
      const normalizedDate = normalizeDate(parseDateAsUTC(date));
      const weekStart = normalizeDate(parseDateAsUTC(state.weekDaysRange[0]));
      const weekEnd = normalizeDate(
        parseDateAsUTC(state.weekDaysRange[state.weekDaysRange.length - 1])
      );
      return normalizedDate >= weekStart && normalizedDate <= weekEnd;
    },
  },
});
